/**
 * LeadSourcesAdmin.tsx — Admin > Lead Sources: lead source categories, each
 * expanding to its lead sources.
 *
 * The reference layout for every Prospera Admin list; its pieces live in
 * components/admin (AdminList, AdminRowActions, useListPagination) so the
 * other lists match it exactly. Clicking a lead source row opens its edit
 * form; a category row expands and collapses from its chevron. Name and Hint
 * sort the categories from their titles (name order to start); each
 * category's sources keep their saved order.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why
 * (R8, R18): no Add, row icons or Actions column; a lead source still opens,
 * as a viewer with its fields read-only and a single Close.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Plus, ChevronRight, ChevronDown, Copy, RefreshCw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminParentRowClass,
  adminChildRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { cn } from '@/lib/utils';
import { toast } from '@/store/toastStore';
import * as adminSvc from '@/services/admin.service';
import type {
  LeadSourceCategory,
  LeadSourceItem,
  UrlParam,
} from '@/services/admin.service';

// ─── Helpers ───────────────────────────────────────────────────────────────────

function isActive(val: string | boolean | undefined): boolean {
  if (typeof val === 'boolean') return val;
  return val === '1' || val === 'true';
}

const DEFAULT_URL_PARAMS: UrlParam[] = Array.from({ length: 5 }, () => ({
  param: '',
  value: '',
}));

const COLUMNS: AdminListColumn[] = [
  { key: 'toggle', className: 'w-10' },
  { key: 'name', header: 'Name', className: 'w-[30%]', sortable: true },
  { key: 'hint', header: 'Hint', sortable: true },
  { key: 'email', header: 'Email Address' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** The category (parent row) sorts. Name is the list's own order; ties keep it. */
const CATEGORY_SORT: ListSortCompare<LeadSourceCategory> = {
  name: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
  hint: (a, b) => compareText(a.hint, b.hint),
};

// ─── Category Modal ────────────────────────────────────────────────────────────

interface CategoryModalProps {
  category?: LeadSourceCategory | null;
  onClose: () => void;
  onSaved: (category: LeadSourceCategory, isNew: boolean) => void;
}

function CategoryModal({ category, onClose, onSaved }: CategoryModalProps) {
  const isEdit = category != null;
  const [name, setName] = useState(category?.name ?? '');
  const [hint, setHint] = useState(category?.hint ?? '');
  const [saving, setSaving] = useState(false);

  const nameError = !name.trim() ? 'Name is required' : undefined;

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (nameError) return;

    setSaving(true);
    try {
      if (isEdit) {
        const updated = await adminSvc.updateLeadSourceCategory(
          category.referralLevel1ID,
          { name: name.trim(), hint },
        );
        onSaved(
          { ...category, ...updated, name: name.trim(), hint },
          false,
        );
        toast.success('Category updated');
      } else {
        const created = await adminSvc.createLeadSourceCategory({
          name: name.trim(),
          hint,
          active: true,
        });
        onSaved({ ...created, leadSources: created.leadSources ?? [] }, true);
        toast.success('Category created');
      }
      onClose();
    } catch {
      toast.error('Failed to save category');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Category' : 'Add Category'}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit()}
            disabled={saving || !!nameError}
            loading={saving}
          >
            {isEdit ? 'Save Changes' : 'Add Category'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Name" required error={nameError}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Category name"
            autoFocus
          />
        </FormField>

        <FormField label="Hint">
          <Textarea
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="Optional hint text"
            rows={3}
          />
        </FormField>
      </form>
    </Modal>
  );
}

// ─── Source Modal ───────────────────────────────────────────────────────────────

interface SourceModalProps {
  source?: LeadSourceItem | null;
  parentCategory: LeadSourceCategory;
  onClose: () => void;
  onSaved: (source: LeadSourceItem, isNew: boolean) => void;
}

function SourceModal({
  source,
  parentCategory,
  onClose,
  onSaved,
}: SourceModalProps) {
  const isEdit = source != null;
  // Locked: a viewer — fields read-only, no Save or Generate, no errors.
  const locked = useAdminPageLevel() === 'locked';
  const [name, setName] = useState(source?.name ?? '');
  const [hint, setHint] = useState(source?.hint ?? '');
  const [urlParams, setUrlParams] = useState<UrlParam[]>(() => {
    if (!source?.urlParams) return DEFAULT_URL_PARAMS.map((p) => ({ ...p }));
    const params = [...source.urlParams];
    while (params.length < 5) params.push({ param: '', value: '' });
    return params.slice(0, 5);
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const nameError = !locked && !name.trim() ? 'Name is required' : undefined;

  const missingValueIndices = locked
    ? []
    : urlParams.map((p, i) => (p.param.trim() && !p.value.trim() ? i : -1)).filter((i) => i >= 0);
  const missingParamIndices = locked
    ? []
    : urlParams.map((p, i) => (!p.param.trim() && p.value.trim() ? i : -1)).filter((i) => i >= 0);
  const urlParamError =
    missingValueIndices.length > 0 || missingParamIndices.length > 0
      ? 'Each URL parameter needs both a name and value'
      : undefined;

  const hasErrors = !!nameError || !!urlParamError;

  function updateUrlParam(idx: number, field: 'param' | 'value', val: string) {
    setUrlParams((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  }

  async function handleGenerateEmail() {
    if (!isEdit) return;
    setGenerating(true);
    try {
      const updated = await adminSvc.updateLeadSourceItem(
        parentCategory.referralLevel1ID,
        source.referralLevel2ID,
        { emailDomain: '' },
      );
      if (updated?.emailDomain) {
        onSaved(
          { ...source, emailDomain: updated.emailDomain },
          false,
        );
        toast.success('Email address generated');
      }
    } catch {
      toast.error('Failed to generate email');
    } finally {
      setGenerating(false);
    }
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (hasErrors || locked) return;

    const payload: Record<string, unknown> = {
      name: name.trim(),
      hint,
      urlParam: urlParams.map((p) => p.param).join('|'),
      urlGetValue: urlParams.map((p) => p.value).join('|'),
      active: true,
    };

    setSaving(true);
    try {
      if (isEdit) {
        const updated = await adminSvc.updateLeadSourceItem(
          parentCategory.referralLevel1ID,
          source.referralLevel2ID,
          payload,
        );
        onSaved(
          {
            ...source,
            ...updated,
            name: name.trim(),
            hint,
            urlParams,
          },
          false,
        );
        toast.success('Lead source updated');
      } else {
        const created = await adminSvc.createLeadSourceItem(
          parentCategory.referralLevel1ID,
          {
            ...payload,
            rootID: parentCategory.referralLevel1ID,
          },
        );
        onSaved(
          {
            ...created,
            accountID: created.accountID ?? parentCategory.accountID,
          },
          true,
        );
        toast.success('Lead source created');
      }
      onClose();
    } catch {
      toast.error('Failed to save lead source');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Lead Source' : isEdit ? 'Edit Lead Source' : 'Add Lead Source'}
      size="md"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit()}
              disabled={saving || hasErrors}
              loading={saving}
            >
              {isEdit ? 'Save Changes' : 'Add Lead Source'}
            </Button>
          </>
        )
      }
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Title" required={!locked} error={nameError}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Lead source name"
              autoFocus
            />
          </FormField>

          {/* Email Address */}
          <FormField
            label="Email Address"
            helpText={
              !isEdit
                ? 'This address will be auto-generated when the record is created.'
                : undefined
            }
          >
            {isEdit ? (
              source.emailDomain ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={source.emailDomain}
                    readOnly
                    disabled
                    className="bg-slate-50"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    iconOnly
                    leftIcon={<Copy className="h-4 w-4" />}
                    onClick={() => {
                      navigator.clipboard.writeText(source.emailDomain);
                      toast.success('Copied to clipboard');
                    }}
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    value=""
                    readOnly
                    disabled
                    placeholder="No email address"
                    className="bg-red-50"
                  />
                  {!locked && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleGenerateEmail}
                      loading={generating}
                      disabled={generating}
                      leftIcon={!generating ? <RefreshCw className="h-4 w-4" /> : undefined}
                    >
                      Generate
                    </Button>
                  )}
                </div>
              )
            ) : (
              <p className="text-sm text-slate-500 italic">
                This address will be auto-generated when the record is created.
              </p>
            )}
          </FormField>

          <FormField label="Hint">
            <Textarea
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="Optional hint text"
              rows={3}
            />
          </FormField>

          {/* URL Parameters */}
          <div
            className={`border rounded-lg p-4 bg-slate-50/50 ${urlParamError ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
          >
            <h4
              className={`text-sm font-semibold mb-3 ${urlParamError ? 'text-red-700' : 'text-slate-700'}`}
            >
              URL Parameters
            </h4>
            <div className="space-y-2">
              {urlParams.map((param, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center"
                >
                  <Input
                    value={param.param}
                    onChange={(e) => updateUrlParam(idx, 'param', e.target.value)}
                    placeholder="Param name"
                    error={
                      missingParamIndices.includes(idx)
                        ? ' '
                        : undefined
                    }
                  />
                  <span className="text-slate-400 text-sm">=</span>
                  <Input
                    value={param.value}
                    onChange={(e) => updateUrlParam(idx, 'value', e.target.value)}
                    placeholder="Value"
                    error={
                      missingValueIndices.includes(idx)
                        ? ' '
                        : undefined
                    }
                  />
                </div>
              ))}
            </div>
            {urlParamError && (
              <p className="text-xs text-red-600 mt-2">{urlParamError}</p>
            )}
          </div>
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function LeadSourcesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [categories, setCategories] = useState<LeadSourceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
    new Set(),
  );

  // Category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editCategory, setEditCategory] = useState<LeadSourceCategory | null>(
    null,
  );

  // Source modal
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editSource, setEditSource] = useState<LeadSourceItem | null>(null);
  const [sourceParentCat, setSourceParentCat] =
    useState<LeadSourceCategory | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'category' | 'source';
    category: LeadSourceCategory;
    source?: LeadSourceItem;
  } | null>(null);

  // ── Data loading ─────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminSvc.listLeadSourceCategories();
      setCategories(data);
    } catch {
      toast.error('Failed to load lead sources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Filtering ────────────────────────────────────────────────────────

  const filteredCategories = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();

    return categories
      .filter((cat) => {
        if (!showDeleted && !isActive(cat.active)) return false;

        if (!q) return true;

        if (cat.name?.toLowerCase().includes(q)) return true;
        if (cat.hint?.toLowerCase().includes(q)) return true;

        const sources = cat.leadSources ?? [];
        return sources.some(
          (s) =>
            s.name?.toLowerCase().includes(q) ||
            s.hint?.toLowerCase().includes(q) ||
            s.emailDomain?.toLowerCase().includes(q),
        );
      })
      .sort(CATEGORY_SORT.name);
  }, [categories, searchQuery, showDeleted]);

  // ── Sorting: the categories (parent rows); sources keep their order ──

  const sorting = useListSort(filteredCategories, { initialKey: 'name', compare: CATEGORY_SORT });

  // ── Pagination (back to page 1 when the search, Show deleted or the sort changes) ─

  const pagination = useListPagination(sorting.sorted, {
    paged: true,
    resetOn: [searchQuery, showDeleted, sorting.sort],
  });
  const totalCategories = pagination.total;

  // ── Toggle helpers ───────────────────────────────────────────────────

  function toggleCategory(id: number) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // ── Local state patchers ─────────────────────────────────────────────

  function patchCategory(
    id: number,
    patch: Partial<LeadSourceCategory>,
  ) {
    setCategories((prev) =>
      prev.map((c) =>
        c.referralLevel1ID === id ? { ...c, ...patch } : c,
      ),
    );
  }

  function patchSource(
    categoryId: number,
    sourceId: number,
    patch: Partial<LeadSourceItem>,
  ) {
    setCategories((prev) =>
      prev.map((c) => {
        if (c.referralLevel1ID !== categoryId) return c;
        return {
          ...c,
          leadSources: (c.leadSources ?? []).map((s) =>
            s.referralLevel2ID === sourceId ? { ...s, ...patch } : s,
          ),
        };
      }),
    );
  }

  // ── Category CRUD callbacks ──────────────────────────────────────────

  function openCreateCategory() {
    setEditCategory(null);
    setShowCategoryModal(true);
  }

  function openEditCategory(cat: LeadSourceCategory) {
    setEditCategory(cat);
    setShowCategoryModal(true);
  }

  function handleCategorySaved(cat: LeadSourceCategory, isNew: boolean) {
    if (isNew) {
      setCategories((prev) => [
        ...prev,
        { ...cat, leadSources: cat.leadSources ?? [] },
      ]);
    } else {
      patchCategory(cat.referralLevel1ID, {
        name: cat.name,
        hint: cat.hint,
      });
    }
  }

  async function handleDeleteCategory() {
    if (!deleteTarget || deleteTarget.type !== 'category') return;
    const cat = deleteTarget.category;
    await adminSvc.deleteLeadSourceCategory(cat.referralLevel1ID);
    patchCategory(cat.referralLevel1ID, { active: '0' });
    toast.success('Category deleted');
  }

  async function handleRestoreCategory(cat: LeadSourceCategory) {
    try {
      await adminSvc.updateLeadSourceCategory(cat.referralLevel1ID, {
        active: true,
      });
      patchCategory(cat.referralLevel1ID, { active: '1' });
      toast.success('Category restored');
    } catch {
      toast.error('Failed to restore category');
    }
  }

  // ── Source CRUD callbacks ────────────────────────────────────────────

  function openCreateSource(cat: LeadSourceCategory) {
    setEditSource(null);
    setSourceParentCat(cat);
    setShowSourceModal(true);
  }

  function openEditSource(source: LeadSourceItem, cat: LeadSourceCategory) {
    setEditSource(source);
    setSourceParentCat(cat);
    setShowSourceModal(true);
  }

  function handleSourceSaved(source: LeadSourceItem, isNew: boolean) {
    if (!sourceParentCat) return;
    const catId = sourceParentCat.referralLevel1ID;

    if (isNew) {
      setCategories((prev) =>
        prev.map((c) => {
          if (c.referralLevel1ID !== catId) return c;
          return {
            ...c,
            leadSources: [...(c.leadSources ?? []), source],
          };
        }),
      );
    } else {
      patchSource(catId, source.referralLevel2ID, source);
    }
  }

  async function handleDeleteSource() {
    if (!deleteTarget || deleteTarget.type !== 'source' || !deleteTarget.source)
      return;
    const { category: cat, source } = deleteTarget;
    await adminSvc.deleteLeadSourceItem(
      cat.referralLevel1ID,
      source.referralLevel2ID,
    );
    patchSource(cat.referralLevel1ID, source.referralLevel2ID, {
      active: '0',
    });
    toast.success('Lead source deleted');
  }

  async function handleRestoreSource(
    source: LeadSourceItem,
    cat: LeadSourceCategory,
  ) {
    try {
      await adminSvc.updateLeadSourceItem(
        cat.referralLevel1ID,
        source.referralLevel2ID,
        { active: true },
      );
      patchSource(cat.referralLevel1ID, source.referralLevel2ID, {
        active: '1',
      });
      toast.success('Lead source restored');
    } catch {
      toast.error('Failed to restore lead source');
    }
  }

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <AdminListPage
      title="Lead Sources"
      subtitle="Manage lead source categories and individual sources."
      actions={
        <Button onClick={openCreateCategory} leftIcon={<Plus className="h-4 w-4" />}>
          Add Category
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name, hint, email…"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="show-deleted"
        />
      }
    >
      {/* Table */}
      {loading ? (
        <SpinnerOverlay label="Loading lead sources…" />
      ) : totalCategories === 0 ? (
        <AdminListEmpty
          title="No lead source categories found"
          hint={searchQuery ? 'Try adjusting your search.' : locked ? undefined : 'Add a category to get started.'}
        />
      ) : (
        <>
          <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} sort={sorting.sort} onSortChange={sorting.onSortChange}>
            {pagination.pageItems.map((cat) => {
              const catId = cat.referralLevel1ID;
              const isExpanded = expandedCategories.has(catId);
              const catDeleted = !isActive(cat.active);
              const isProtected = cat.name?.toLowerCase() === 'established patient';
              const sources = cat.leadSources ?? [];
              const activeSources = sources.filter((s) => isActive(s.active));
              const visibleSources = showDeleted ? sources : activeSources;
              const hasChildren = visibleSources.length > 0;
              const showChildren = isExpanded && hasChildren;

              return (
                <React.Fragment key={catId}>
                  {/* Category row — a click anywhere on it expands or collapses it. A
                      category with no sources to show has no chevron and no toggle. */}
                  <tr
                    className={adminParentRowClass({ deleted: catDeleted, expandable: hasChildren })}
                    data-category-id={catId}
                    onClick={hasChildren ? () => toggleCategory(catId) : undefined}
                  >
                    <td className={ADMIN_TD_CLASS}>
                      {hasChildren ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategory(catId);
                          }}
                          className="text-slate-400 hover:text-slate-600"
                          aria-expanded={showChildren}
                          aria-label={`${showChildren ? 'Collapse' : 'Expand'} ${cat.name}`}
                        >
                          {showChildren ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      ) : (
                        <span className="inline-block w-4" />
                      )}
                    </td>
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowName deleted={catDeleted}>{cat.name}</AdminRowName>
                    </td>
                    <td className={cn(ADMIN_TD_CLASS, 'truncate')}>{cat.hint || '—'}</td>
                    <td className={ADMIN_TD_CLASS} />
                    {!locked && (
                      <td className={ADMIN_TD_CLASS}>
                        {!isProtected && (
                          <AdminRowActions>
                            <AdminRowAction
                              kind="add"
                              title="Add source"
                              label={`Add source to ${cat.name}`}
                              onClick={() => openCreateSource(cat)}
                            />
                            <AdminRowAction
                              kind="edit"
                              title="Edit category"
                              label={`Edit ${cat.name}`}
                              onClick={() => openEditCategory(cat)}
                            />
                            {catDeleted ? (
                              <AdminRowAction
                                kind="restore"
                                label={`Restore ${cat.name}`}
                                onClick={() => void handleRestoreCategory(cat)}
                              />
                            ) : (
                              <AdminRowAction
                                kind="delete"
                                label={`Delete ${cat.name}`}
                                onClick={() => setDeleteTarget({ type: 'category', category: cat })}
                              />
                            )}
                          </AdminRowActions>
                        )}
                      </td>
                    )}
                  </tr>

                  {/* Source rows — a click opens the source's edit form */}
                  {showChildren &&
                    visibleSources
                      .sort((a, b) => (a.rl2Order ?? 0) - (b.rl2Order ?? 0))
                      .map((source) => {
                        const srcDeleted = !isActive(source.active);
                        return (
                          <tr
                            key={source.referralLevel2ID}
                            data-source-id={source.referralLevel2ID}
                            onClick={() => openEditSource(source, cat)}
                            className={adminChildRowClass({ deleted: srcDeleted })}
                          >
                            <td className={ADMIN_TD_CLASS} />
                            <td className={cn(ADMIN_TD_CLASS, 'pl-10')}>
                              <AdminRowName deleted={srcDeleted} tone="child">
                                {source.name}
                              </AdminRowName>
                            </td>
                            <td className={cn(ADMIN_TD_CLASS, 'truncate')}>{source.hint || '—'}</td>
                            <td className={ADMIN_TD_CLASS}>
                              {source.emailDomain ? (
                                <div className="flex items-center gap-1 min-w-0">
                                  <span className="text-xs text-slate-500 truncate" title={source.emailDomain}>
                                    {source.emailDomain}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      // Copies only; the row's click would open the form.
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(source.emailDomain);
                                      toast.success('Copied to clipboard');
                                    }}
                                    className="text-slate-400 hover:text-slate-600 shrink-0"
                                    title="Copy email address"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">—</span>
                              )}
                            </td>
                            {!locked && (
                              <td className={ADMIN_TD_CLASS}>
                                <AdminRowActions>
                                  <AdminRowAction
                                    kind="edit"
                                    label={`Edit ${source.name}`}
                                    onClick={() => openEditSource(source, cat)}
                                  />
                                  {srcDeleted ? (
                                    <AdminRowAction
                                      kind="restore"
                                      label={`Restore ${source.name}`}
                                      onClick={() => void handleRestoreSource(source, cat)}
                                    />
                                  ) : (
                                    <AdminRowAction
                                      kind="delete"
                                      label={`Delete ${source.name}`}
                                      onClick={() => setDeleteTarget({ type: 'source', category: cat, source })}
                                    />
                                  )}
                                </AdminRowActions>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                </React.Fragment>
              );
            })}
          </AdminListTable>

          {/* Pagination */}
          <PaginationFooter pagination={pagination} noun={['category', 'categories']} />
        </>
      )}

      {/* Category Create/Edit Modal */}
      {showCategoryModal && (
        <CategoryModal
          category={editCategory}
          onClose={() => setShowCategoryModal(false)}
          onSaved={handleCategorySaved}
        />
      )}

      {/* Source Create/Edit Modal */}
      {showSourceModal && sourceParentCat && (
        <SourceModal
          source={editSource}
          parentCategory={sourceParentCat}
          onClose={() => setShowSourceModal(false)}
          onSaved={handleSourceSaved}
        />
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        title={
          deleteTarget?.type === 'category'
            ? 'Delete Category'
            : 'Delete Lead Source'
        }
        message={
          deleteTarget?.type === 'category'
            ? `Are you sure you want to delete "${deleteTarget.category.name}"? This will also hide all lead sources under it.`
            : deleteTarget?.source
              ? `Are you sure you want to delete "${deleteTarget.source.name}"?`
              : ''
        }
        confirmLabel="Delete"
        variant="danger"
        onConfirm={
          deleteTarget?.type === 'category'
            ? handleDeleteCategory
            : handleDeleteSource
        }
        onClose={() => setDeleteTarget(null)}
      />
    </AdminListPage>
  );
}
